import { useEffect, useState } from 'react'
import { api, errorText } from '../api/client'
import type { CallResult } from '../api/types'
import { phoneText } from '../lib/format'
import { hangUp, placeCall, type SoftphoneState } from '../lib/softphone'
import { Icon } from './Icon'
import { Modal } from './Modal'
import { useToast } from './Toast'

/** One call, from the record you are already looking at.
 *
 *  The dialer is a queue: a telemarketer works down a list of hundreds. A loan
 *  officer calling one person they have open does not want a different screen and
 *  a list — they want the phone. Same softphone underneath (buckingham.crm.voice,
 *  lib/softphone), same dispositions, no queue.
 *
 *  The outcome is recorded whether or not the browser dialled: an officer who
 *  picked up their own handset still needs the call on the timeline, and the lead
 *  platform's own Log-call box worked that way. */

const OUTCOMES: [string, string][] = [
  ['connected', 'Connected'],
  ['no_answer', 'No answer'],
  ['voicemail', 'Left voicemail'],
  ['callback', 'Callback scheduled'],
  ['not_interested', 'Not interested'],
  ['wrong_number', 'Wrong number'],
]

const LINE: Record<SoftphoneState, string> = {
  offline: 'Microphone not connected yet',
  ready: 'Ready',
  connecting: 'Connecting…',
  'on-call': 'On the call',
  error: 'The line failed',
}

export function CallModal({ contactId, name, phone, allowed, reason,
                            onClose, onLogged }: {
  contactId: number
  name: string
  /** Their best number, for the officer to see before anything rings. */
  phone?: string | null
  allowed: boolean
  reason?: string
  onClose: () => void
  onLogged?: () => void
}) {
  const [call, setCall] = useState<CallResult | null>(null)
  const [line, setLine] = useState<SoftphoneState>('offline')
  const [outcome, setOutcome] = useState('connected')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState('')
  const toast = useToast()

  // Leaving the dialog with a call up would leave it up with nothing driving it.
  useEffect(() => () => { hangUp() }, [])

  async function dial() {
    setBusy('dial')
    try {
      const res = await api.startCall(contactId)
      setCall(res)
      if (res.status === 'simulated') {
        toast('Logged — calling is simulated, nothing was dialled', { tone: 'neutral' })
      } else {
        // The browser joining the conference is what makes the server ring them,
        // so the officer is on the line before their phone does.
        await placeCall(res.call_id, (s, detail) => {
          setLine(s)
          if (s === 'error' && detail) toast(detail, { tone: 'danger' })
        })
      }
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy('')
    }
  }

  async function log() {
    setBusy('log')
    try {
      if (call) await api.disposeCall(call.call_id, { disposition: outcome, notes: notes || undefined })
      else await api.logActivity({ contact_id: contactId }, { kind: 'call', outcome, body: notes || undefined })
      hangUp()
      toast('Call logged', { tone: 'positive' })
      onLogged?.()
      onClose()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
      setBusy('')
    }
  }

  return (
    <Modal open width={460} title={`Call ${name}`} onClose={onClose}
      subtitle={phone ? phoneText(phone) : 'No number on file'}
      footer={
        <>
          <button className="btn" onClick={onClose}>Close</button>
          <button className="btn primary" onClick={log} disabled={busy !== ''}>
            <Icon name="check" size={16} /> {busy === 'log' ? 'Saving…' : 'Log the call'}
          </button>
        </>
      }>
      <div className="stack">
        {!allowed ? (
          <p className="quiet" style={{ margin: 0, fontSize: 12 }}>
            {reason || 'This number cannot be called.'} You can still record a call you made
            another way.
          </p>
        ) : null}

        <div className="call-line">
          {call && call.status !== 'simulated' ? (
            <>
              <span className={`call-dot ${line}`} />
              <span>{LINE[line]}</span>
              <span className="spacer" />
              <button className="btn sm danger" onClick={() => { hangUp(); setLine('ready') }}>
                <Icon name="call_end" size={15} /> Hang up
              </button>
            </>
          ) : (
            <>
              <Icon name="call" size={17} />
              <span>{call ? 'Simulated — nothing was dialled' : 'Call through the browser'}</span>
              <span className="spacer" />
              <button className="btn primary sm" onClick={dial} disabled={!allowed || busy !== '' || !!call}>
                {busy === 'dial' ? 'Dialling…' : 'Call now'}
              </button>
            </>
          )}
        </div>

        <label className="fl">
          <span>Outcome</span>
          <select className="field" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
            {OUTCOMES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="fl">
          <span>Notes</span>
          <textarea className="field" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="What was discussed, next steps…" />
        </label>
      </div>
    </Modal>
  )
}
